import { authFetch } from '@/auth/authFetch'

/**
 * Client for the controller's fleet API.
 *
 * Routed through the gateway at /control/* (see nginx gateway.conf), so no
 * separate origin and no CORS. Every call carries the operator's access token
 * via authFetch, which also handles the silent-refresh-on-401 dance.
 */

function readEnv(key: string): string | undefined {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const v = (import.meta as any)?.env?.[key]
    return typeof v === 'string' && v.length > 0 ? v : undefined
  } catch {
    return undefined
  }
}

const CONTROL_BASE = (readEnv('VITE_CONTROL_BASE_URL') ?? '/control').replace(/\/+$/, '')

export type AgentMode = 'live' | 'replay' | 'idle'

export interface Capacity {
  maxLiveSessions: number
  maxClients: number
  basis: 'measured' | 'estimated'
  perSessionMemBytes: number | null
  perClientMemBytes: number | null
}

export interface HostSample {
  tsMs: number
  cpuPct: number | null
  cpuCount: number
  memTotalBytes: number
  memUsedBytes: number
  memUsedPct: number
  procRssBytes: number
  uptimeSec: number
  vramUsedBytes?: number | null
  vramTotalBytes?: number | null
  vramUsedPct?: number | null
}

export interface FleetAgent {
  agentId: string
  hostname: string
  protocolVersion: 2 | 3
  connected: boolean
  lastSeen: number
  capacity: Capacity
  modes: AgentMode[]
  desired: { mode: AgentMode; clientCount: number; vmsWsUrl: string }
  actual: {
    mode: AgentMode | null
    clientCount: number
    fleetState: string
    summary: {
      total: number; playing: number; starting: number
      recovering: number; errored: number; idle: number
    }
  }
  host: HostSample | null
  /** False when an agent has not yet caught up with desired state. */
  converged: boolean
}

export interface FleetView {
  agents: FleetAgent[]
  totals: {
    agents: number
    connected: number
    liveCapacity: number
    replayCapacity: number
    desiredClients: number
    actualClients: number
  }
}

export interface ProjectionBasis {
  sampleCount: number
  observedClients: number | null
  extrapolationFactor: number | null
  confidence: 'measured' | 'interpolated' | 'extrapolated' | 'insufficient-data'
}

export interface Projections {
  targetClients: number
  mode: AgentMode
  capacity: {
    connectedAgents: number
    liveCapacity: number
    replayCapacity: number
    perSessionMemBytes: number | null
    shortfall: number | null
    additionalAgentsNeeded: number | null
    basis: ProjectionBasis
  }
  throughput: {
    messagesPerSec: number | null
    bytesPerSecToKafka: number | null
    avgMessageBytes: number | null
    kafkaGbPerDay: number | null
    basis: ProjectionBasis
  }
  storage: {
    streamRowsPerWindow: { low: number; high: number } | null
    dimensionRowsPerWindow: { low: number; high: number } | null
    bytesPerRow: number | null
    gbPerDayLow: number | null
    gbPerDayHigh: number | null
    steadyStateGbLow: number | null
    steadyStateGbHigh: number | null
    basis: ProjectionBasis
    notes: string[]
  }
}

/** Error carrying the server's structured detail, so the UI can show the real reason. */
export class FleetApiError extends Error {
  // Declared as fields rather than constructor parameter properties: the app
  // builds with `erasableSyntaxOnly`, which forbids the shorthand.
  readonly status: number
  readonly detail?: Record<string, unknown>

  constructor(message: string, status: number, detail?: Record<string, unknown>) {
    super(message)
    this.name = 'FleetApiError'
    this.status = status
    this.detail = detail
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${CONTROL_BASE}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  })

  if (!res.ok) {
    let detail: Record<string, unknown> | undefined
    let message = `${res.status} ${res.statusText}`
    try {
      detail = await res.json()
      // The API's over-capacity 422 carries a full explanation — surface it
      // verbatim rather than replacing it with a generic failure message.
      if (detail && typeof detail.error === 'string') message = detail.error
    } catch {
      /* non-JSON error body */
    }
    throw new FleetApiError(message, res.status, detail)
  }

  return res.json() as Promise<T>
}

export interface SetStateBody {
  mode: AgentMode
  clientCount: number
  vmsWsUrl?: string
  appUrl?: string
  datasetId?: string
  scenarioIds?: string[]
  seed?: number
}

export const fleetClient = {
  getFleet: () => request<FleetView>('/fleet'),

  getProjections: (clients: number, mode: AgentMode = 'replay', lookbackMinutes = 60) =>
    request<Projections>(
      `/fleet/projections?clients=${clients}&mode=${mode}&lookback=${lookbackMinutes}`,
    ),

  setFleetTarget: (body: SetStateBody) =>
    request<{
      mode: AgentMode
      requested: number
      allocated: number
      agents: { agentId: string; allocated: number; delivered: boolean }[]
    }>('/fleet/target', { method: 'PUT', body: JSON.stringify(body) }),

  setAgentState: (agentId: string, body: SetStateBody) =>
    request<{ agentId: string; desired: SetStateBody; delivered: boolean }>(
      `/agents/${encodeURIComponent(agentId)}/state`,
      { method: 'PUT', body: JSON.stringify(body) },
    ),

  reloadInstance: (agentId: string, instanceId: string) =>
    request<{ status: string }>(
      `/agents/${encodeURIComponent(agentId)}/reload/${encodeURIComponent(instanceId)}`,
      { method: 'POST' },
    ),

  startCapture: (agentId: string, dir?: string) =>
    request<{
      delivered: boolean
      ok?: boolean
      capturing?: boolean
      path?: string
      error?: string
    }>(`/agents/${encodeURIComponent(agentId)}/capture/start`, {
      method: 'POST',
      body: JSON.stringify(dir ? { dir } : {}),
    }),

  stopCapture: (agentId: string) =>
    request<{
      delivered: boolean
      ok?: boolean
      capturing?: boolean
      path?: string
      error?: string
    }>(`/agents/${encodeURIComponent(agentId)}/capture/stop`, {
      method: 'POST',
      body: '{}',
    }),
}
